import { Container, HeaderTitle, StyledCalendarIcon, RightPane, Text, LeftPane, Content, BelowTitle } from './styles';
import React, { ReactNode } from 'react';
import { ArrowBackLink } from '../arrow-back-link';
import { faCalendar } from '@fortawesome/pro-light-svg-icons/faCalendar';
import { useIntl } from 'react-intl';

interface ResourceBookingHeaderProps {
  withBackButton: boolean;
  /**
   * The page's name. Optional: a host that draws its own header is already showing it, and a frame
   * that repeated it would name the page twice. The row still lays out around the gap -- see below.
   */
  title?: string;
  onBackClick?: VoidFunction;
  onMyBookingsClick?: VoidFunction;
  shouldHideMyBookingsButton?: boolean;
  /**
   * The lead-in above the title. Dropped when a host draws its own chrome directly above this frame
   * -- the portal's breadcrumb bar -- where the header's own 55px would read as a gap between the
   * two rather than as the top of a page.
   */
  withTopPadding?: boolean;
  /**
   * A control for the right of the header, opposite the title.
   *
   * Shares the slot the built-in my-bookings link uses, and the two are mutually exclusive: a page
   * passing its own action is replacing that link, not sitting beside it.
   */
  rightAction?: ReactNode;
  /**
   * A full-width row beneath the title, for controls that belong to the page rather than a column of
   * it. Supplying it makes the header drop its own bottom rule and bottom padding, so whatever is
   * passed must draw a bottom border of its own.
   */
  belowTitle?: ReactNode;
}

export const ResourceBookingHeader = ({
  withBackButton,
  title,
  onBackClick,
  onMyBookingsClick,
  shouldHideMyBookingsButton,
  withTopPadding = true,
  rightAction,
  belowTitle,
}: ResourceBookingHeaderProps): JSX.Element => {
  const intl = useIntl();

  return (
    <Container $withTopPadding={withTopPadding} $withBelowTitle={!!belowTitle} data-testid="resource-booking-header">
      {withBackButton && <ArrowBackLink onClick={onBackClick} />}
      <Content>
        {/*
          ⚠️ RENDERED EVEN WITH NOTHING IN IT. `Content` is `justify-content: space-between`, which
          with a single child lays it out at the START -- so dropping this pane for a titleless
          header would not centre the action, it would pull it to the left edge. The empty pane is
          what keeps a lone right-hand control on the right.
        */}
        <LeftPane>
          <HeaderTitle data-testid="desktop-header">{title}</HeaderTitle>
        </LeftPane>
        {rightAction}
        {!rightAction && !shouldHideMyBookingsButton && (
          <RightPane onClick={onMyBookingsClick}>
            <StyledCalendarIcon icon={faCalendar} />
            <Text>{intl.formatMessage({ id: 'my_bookings' })}</Text>
          </RightPane>
        )}
      </Content>
      {!!belowTitle && <BelowTitle data-testid="resource-booking-header-below-title">{belowTitle}</BelowTitle>}
    </Container>
  );
};
